import React, { forwardRef } from 'react';

/**
 * Checkbox Component - Atomic Level
 * 
 * An accessible checkbox component with custom styling
 * and label support.
 * 
 * Features:
 * - Custom styling
 * - Label support
 * - Disabled state
 * - Indeterminate state
 * - Accessible (ARIA attributes)
 * - Helper text support
 */

export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Label text for the checkbox */
  label?: string;
  /** Helper text to display below the checkbox */
  helperText?: string;
  /** Error message */
  error?: string;
  /** Indeterminate state */
  indeterminate?: boolean;
  /** Container class name */
  containerClassName?: string;
  /** Label class name */
  labelClassName?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      label,
      helperText,
      error,
      indeterminate = false,
      containerClassName = '',
      labelClassName = '',
      disabled,
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const checkboxId = id || `checkbox-${Math.random().toString(36).substr(2, 9)}`;
    
    // Handle indeterminate state
    React.useEffect(() => {
      if (ref && typeof ref !== 'function' && ref.current) {
        ref.current.indeterminate = indeterminate;
      }
    }, [ref, indeterminate]);

    return (
      <div className={`flex items-start ${containerClassName}`}>
        <div className="flex items-center h-5">
          <input
            ref={ref}
            id={checkboxId}
            type="checkbox"
            disabled={disabled}
            aria-describedby={
              error ? `${checkboxId}-error` : 
              helperText ? `${checkboxId}-helper` : 
              undefined
            }
            aria-invalid={!!error}
            className={`
              w-4 h-4 rounded border-[#CCCCCC] 
              text-[#EAB308] 
              focus:ring-2 focus:ring-[#EAB308]/20 focus:ring-offset-0
              transition-all duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              cursor-pointer
              ${error ? 'border-red-500' : ''}
              ${className}
            `}
            {...props}
          />
        </div>
        
        {(label || helperText || error) && (
          <div className="ml-3 text-sm">
            {label && (
              <label
                htmlFor={checkboxId}
                className={`
                  font-medium text-gray-700 cursor-pointer select-none
                  ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${labelClassName}
                `}
              >
                {label}
              </label>
            )}
            
            {error && (
              <p id={`${checkboxId}-error`} className="mt-1 text-sm text-red-600">
                {error}
              </p>
            )}
            
            {helperText && !error && (
              <p id={`${checkboxId}-helper`} className="mt-1 text-sm text-gray-500">
                {helperText}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;

